import React, { useMemo } from 'react'
import styled from 'styled-components'

import { BaseWrapper, H3, Link } from '@base'
import { Devices, Spacings, Text } from '@styles'
import { displayAmount } from '@utils'
import { Heading } from '../style'
import { IconWrapper, ViewInfoCard } from '../../CompanyReport'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { getInputType } from '@base/composite/Table/TableUtils/Columns'
import { paths } from '@routes'

export const Wapper = styled(BaseWrapper)`
	display: flex;
	padding: ${Spacings.SPACING_4B};
	justify-content: space-between;
	align-items: center;
	flex: 8;
	gap: ${Spacings.SPACING_4B};
	flex-direction: column;
	@media ${Devices.mobile} {
		flex-direction: row;
		align-items: flex-start;
	}
`

const ContentWapper = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${Spacings.SPACING_4B};
`

export const Divider = styled.hr`
	border: none;
	background: ${({ theme }) => theme.palette.background.disabled};
	width: 100%;
	height: 1px;
	margin: 0 ${Spacings.SPACING_4B};
	@media ${Devices.mobile} {
		width: 3px;
		height: auto;
		align-self: stretch;
		margin: 0 ${Spacings.SPACING_4B};
	}
`

const Typography = styled.p`
	color: ${({ theme }) => theme.palette.text.greyTwo};
	font-family: Poppins;
	font-size: ${Text.MEDIUM};
	font-style: normal;
	font-weight: ${Text.BOLD_500};
	line-height: ${Spacings.SPACING_5B};
`

const ValueWapper = styled(H3)`
	color: ${({ theme }) => theme.palette.text.greyThree};
	font-family: Poppins;
	font-size: ${Text.XX_LARGE};
	font-style: normal;
	font-weight: ${Text.BOLD_500};
	line-height: ${Spacings.SPACING_8B};

	span {
		color: ${({ theme }) => theme.palette.text.greyTwo};
		font-family: Poppins;
		font-size: ${Text.SMALL};
		font-style: normal;
		font-weight: ${Text.BOLD_500};
		line-height: ${Spacings.SPACING_4B};
	}
`

const ViewInfoCardWapper = styled(ViewInfoCard)`
	position: unset;
`

const ItemWapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
`

const ItemRow = styled.div`
	display: flex;
	align-items: center;
`

export const ItemIconWrapper = styled(IconWrapper)`
	font-size: 0px;
`

/* ---------- Helpers ---------- */

export const buildNormalized = (arr) =>
	(arr || []).map((item) => ({
		unit_type: item?.unit_type,
		key: item?.unit_type,
		value:
			item?.unit_type === 'WEIGHT'
				? `${item?.value_in_mt ?? item?.total_value ?? '-'} ${getInputType(item?.unit_type)}`
				: `${item?.total_value ?? '-'} ${getInputType(item?.unit_type)}`,
	}))

/* ---------- Presentational subcomponents ---------- */

export const HoverDetails = ({ hoverItems }) => {
	if (!hoverItems || !hoverItems.length) return null
	return (
		<ViewInfoCardWapper>
			<span>
				{hoverItems.map((it, idx) => (
					<div key={idx}>
						{it.key}: <b>{it.value}</b>
					</div>
				))}
			</span>
		</ViewInfoCardWapper>
	)
}

const MetricItem = ({ name, value, unit, hoverItems = [], clickable = false, linkhref }) => (
	<ItemWapper>
		<ItemRow>
			<Typography>{name}</Typography>
			{!!hoverItems.length && (
				<ItemIconWrapper>
					<InfoOutlinedIcon fontSize='small' />
					<HoverDetails hoverItems={hoverItems} />
				</ItemIconWrapper>
			)}
		</ItemRow>
		{clickable ? (
			<Link to={`${linkhref}`} rel='noopener noreferrer' target='_blank'>
				<ValueWapper>
					{value} <span>{unit}</span>
				</ValueWapper>
			</Link>
		) : (
			<ValueWapper>
				{value} <span>{unit}</span>
			</ValueWapper>
		)}
	</ItemWapper>
)

export const MetricSection = ({ title, items = [] }) => (
	<ContentWapper>
		<Heading>{title}</Heading>
		<Content>
			{items.map((it, idx) => (
				<MetricItem
					key={`${it.name}-${idx}`}
					name={it.name}
					value={it.value}
					unit={it.unit}
					hoverItems={it.hoverItems}
					clickable={it.clickable}
					linkhref={it.linkhref}
				/>
			))}
		</Content>
	</ContentWapper>
)

/* ---------- Main component ---------- */

const EnquiryPaymentOrder = ({
	EnquiryData,
	NewUserOnboarded,
	QuotesGiven,
	ReceivedYesterday,
	ExpectedDays,
	TotalOrders,
	OrderedWeight,
	OrderBook,
	ServicedMonth,
	ServicedWeight,
	TotalPendingOrder,
}) => {
	const enquiryWeightItem = useMemo(() => EnquiryData?.find((i) => i.unit_type === 'WEIGHT'), [EnquiryData])
	const quotesWeightItem = useMemo(() => QuotesGiven?.find((i) => i.unit_type === 'WEIGHT'), [QuotesGiven])

	const { _enquiryData, _quotesGiven, _orderedWeight, _totalPendingOrder, _servicedWeight } = useMemo(
		() => ({
			_enquiryData: buildNormalized(EnquiryData),
			_quotesGiven: buildNormalized(QuotesGiven),
			_orderedWeight: buildNormalized(OrderedWeight),
			_totalPendingOrder: buildNormalized(TotalPendingOrder),
			_servicedWeight: buildNormalized(ServicedWeight),
		}),
		[EnquiryData, QuotesGiven, OrderedWeight, TotalPendingOrder, ServicedWeight]
	)

	const enquiryData = useMemo(
		() => [
			{
				name: 'Enquiry',
				value: enquiryWeightItem?.total_value ?? '-',
				unit: 'MT',
				hoverItems: _enquiryData || [],
			},
			{
				name: 'New User Onboarded',
				value: NewUserOnboarded ?? '-',
				unit: '',
				hoverItems: [],
			},
			{
				name: 'Quotes Submitted',
				value: quotesWeightItem?.total_value ?? '-',
				unit: 'MT',
				hoverItems: _quotesGiven || [],
			},
		],
		[enquiryWeightItem, _enquiryData, NewUserOnboarded, quotesWeightItem, _quotesGiven]
	)

	const paymentsData = useMemo(
		() => [
			{
				name: 'Received yesterday',
				value: displayAmount(ReceivedYesterday) ?? '-',
				unit: 'Cr',
				hoverItems: [],
			},
			{
				name: 'Expected (Next n days)',
				value: displayAmount(ExpectedDays) ?? '-',
				unit: 'Cr',
				hoverItems: [],
			},
			{
				name: 'Total Avg. Payment Delays',
				value: '-',
				unit: 'Days',
				hoverItems: [],
			},
		],
		[ReceivedYesterday, ExpectedDays]
	)

	const ordersData = useMemo(
		() => [
			{
				name: 'Total orders',
				value: TotalOrders ?? '-',
				unit: 'MT',
				hoverItems: _orderedWeight || [],
			},
			{
				name: 'Order book (pending)',
				value: OrderBook ?? '-',
				unit: 'MT',
				hoverItems: _totalPendingOrder || [],
				clickable: true,
				linkhref: paths.admin_manage_enquiry_orders_order_book,
			},
			{
				name: 'Serviced',
				value: ServicedMonth ?? '-',
				unit: 'MT',
				hoverItems: _servicedWeight || [],
			},
		],
		[TotalOrders, _orderedWeight, OrderBook, _totalPendingOrder, ServicedMonth, _servicedWeight]
	)

	return (
		<Wapper>
			<MetricSection title='Enquiry' items={enquiryData} />
			<Divider />
			<MetricSection title='Payments' items={paymentsData} />
			<Divider />
			<MetricSection title='Orders' items={ordersData} />
		</Wapper>
	)
}

export default EnquiryPaymentOrder
